/**
 * WO-D2.M — user / role / seat administration (users.manage).
 *
 * WO-M0 — rebuilt on the design system: Screen / Card / Field / Button / ListRow / Badge / Text.
 */
import { useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet, View } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listUsers, inviteUser, setUserRole, setUserActive } from '../../../../src/api/account';
import { ApiRequestError } from '../../../../src/api/client';
import { Screen, Text, Card, Field, Button, ListRow } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';

const ROLES = ['owner', 'office', 'staff', 'consultant'] as const;

export default function UsersScreen() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['account-users'], queryFn: listUsers });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof ROLES)[number]>('staff');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // REM-DEBT-07 — id of the row whose role/active mutation is in flight, so a rapid double-tap on a
  // row action can't fire the request twice (the invite button already had this guard; the rows did not).
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  async function onInvite() {
    setBusy(true);
    setInviteLink(null);
    try {
      const res = await inviteUser({ email, role });
      setInviteLink(res.inviteToken);
      setEmail('');
      qc.invalidateQueries({ queryKey: ['account-users'] });
      Alert.alert('Invite created', 'Share the set-password link with your teammate (email send is configured under Senders).');
    } catch (e) {
      Alert.alert('Could not invite', e instanceof ApiRequestError ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  async function cycleRole(id: string, current: string | null) {
    if (rowBusy) return; // REM-DEBT-07 — guard double-submit
    setRowBusy(id);
    const idx = ROLES.indexOf((current as (typeof ROLES)[number]) ?? 'staff');
    const next = ROLES[(idx + 1) % ROLES.length];
    try {
      await setUserRole(id, next);
      qc.invalidateQueries({ queryKey: ['account-users'] });
    } catch (e) {
      Alert.alert('Could not change role', e instanceof ApiRequestError ? e.message : 'Error');
    } finally {
      setRowBusy(null);
    }
  }

  async function toggleActive(id: string, active: boolean) {
    if (rowBusy) return; // REM-DEBT-07 — guard double-submit
    setRowBusy(id);
    try {
      await setUserActive(id, !active);
      qc.invalidateQueries({ queryKey: ['account-users'] });
    } catch (e) {
      Alert.alert('Could not update', e instanceof ApiRequestError ? e.message : 'Error');
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <Screen scroll>
      <Text variant="title">Users & roles</Text>

      <Card>
        <Text variant="label" color="secondary">Invite a teammate</Text>
        <Field
          label="Email"
          value={email}
          onChangeText={setEmail}
          placeholder="email@company.com"
          autoCapitalize="none"
          keyboardType="email-address"
          textContentType="emailAddress"
          autoComplete="email"
          editable={!busy}
          accessibilityLabel="Teammate email address"
        />
        <View style={styles.roleRow}>
          {ROLES.map((r) => (
            <Button
              key={r}
              title={r}
              variant={role === r ? 'primary' : 'secondary'}
              size="sm"
              fullWidth={false}
              onPress={() => setRole(r)}
              accessibilityLabel={`Select role ${r}`}
            />
          ))}
        </View>
        <Button
          title="Send invite"
          variant="primary"
          size="md"
          onPress={onInvite}
          loading={busy}
          disabled={busy || !email}
          accessibilityLabel="Send invite"
        />
        {inviteLink ? (
          <Text variant="data" color="link" selectable accessibilityLabel="Invite token">
            token: {inviteLink.slice(0, 24)}…
          </Text>
        ) : null}
      </Card>

      <Text variant="label" color="secondary">Team</Text>
      {users.isLoading ? (
        <ActivityIndicator accessibilityLabel="Loading team members" />
      ) : null}
      <View style={styles.list}>
        {users.data?.map((u) => (
          <ListRow
            key={u.id}
            title={`${u.full_name}${!u.activated ? ' (pending)' : ''}`}
            subtitle={u.email}
            right={
              <View style={styles.actions}>
                <Button
                  title={u.role ?? '—'}
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
              loading={rowBusy === u.id}
              disabled={rowBusy !== null}
                  onPress={() => cycleRole(u.id, u.role)}
                  accessibilityLabel={`Change role for ${u.full_name}`}
                />
                {u.is_active ? (
                  <Button
                    title="Disable"
                    variant="danger"
                    size="sm"
                    fullWidth={false}
                    disabled={rowBusy !== null}
                    onPress={() => toggleActive(u.id, u.is_active)}
                    accessibilityLabel={`Disable ${u.full_name}`}
                  />
                ) : (
                  <Button
                    title="Enable"
                    variant="secondary"
                    size="sm"
                    fullWidth={false}
                    disabled={rowBusy !== null}
                    onPress={() => toggleActive(u.id, u.is_active)}
                    accessibilityLabel={`Enable ${u.full_name}`}
                  />
                )}
              </View>
            }
          />
        ))}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  roleRow: { flexDirection: 'row', gap: theme.spacing.sm, flexWrap: 'wrap' },
  list: { gap: theme.spacing.sm },
  actions: { flexDirection: 'row', gap: theme.spacing.sm, alignItems: 'center' },
});
